import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Typography, Box, Grid, CircularProgress } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import WorldMap from 'react-svg-worldmap';
import { FEATURES } from '../../config/features';
import {
  acme,
  acmeNeutralColorScale,
  acmeChartColors,
  acmeDashboardStatGridSx,
  acmeDashboardCardContentSx,
  acmeDashboardPanelCardSx,
  acmeDashboardSectionTitleSx,
  acmeDashboardSectionDescSx,
  DASHBOARD_STAT_GRID_SPACING,
  DASHBOARD_CHART_GRID_SPACING,
} from '../../theme/acmeTheme';
import { Card, CardContent, StatCard, Button, trafficMetricIcons } from '../ui';

const metricNames = {
  viewPage: 'Unique Visitors',
  registerUsers: 'Register Users',
  buyPlan: 'Buy Plan',
  projects: 'Projects',
  allMoney: 'All Money',
};

const panelCardSx = acmeDashboardPanelCardSx;

const DashboardTraffics = ({ dateRange, calculateChange, calculateRate }) => {
  const statGridMd = FEATURES.PROJECTS_ENABLED ? 2.4 : 3;
  const [loading, setLoading] = useState(true);
  const [countryDataLoading, setCountryDataLoading] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState('viewPage');
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [trafficStats, setTrafficStats] = useState({
    viewPage: { count: 0, rate: 0, loading: false },
    registerUsers: { count: 0, rate: 0, loading: false },
    buyPlan: { count: 0, rate: 0, loading: false },
    projects: { count: 0, rate: 0, loading: false },
    allMoney: { amount: 0, rate: 0, loading: false, currency: '€' },
  });
  const [countryData, setCountryData] = useState([]);
  const [chartData, setChartData] = useState([]);

  const formatDateParam = (date) => {
    if (date?.format) return date.format('YYYY-MM-DD');
    if (date instanceof Date) return date.toISOString().split('T')[0];
    return date || new Date().toISOString().split('T')[0];
  };

  const getDateParams = () => ({
    startDate: formatDateParam(dateRange?.startDate),
    endDate: formatDateParam(dateRange?.endDate),
  });

  const fetchTrafficStats = async () => {
    try {
      setTrafficStats((prev) =>
        Object.fromEntries(
          Object.keys(prev).map((k) => [k, { ...prev[k], loading: true }]),
        ),
      );

      const params = getDateParams();
      const response = await axios.get('/api/dashboard/traffic-stats', { params });
      const currentData = response.data || {};

      setTrafficStats({
        viewPage: {
          count: currentData.viewPage || 0,
          rate: calculateRate(currentData.viewPage || 0, currentData.viewPage || 0),
          loading: false,
        },
        registerUsers: {
          count: currentData.registerUsers || 0,
          rate: calculateRate(currentData.registerUsers || 0, currentData.registerUsers || 0),
          loading: false,
        },
        buyPlan: {
          count: currentData.buyPlan || 0,
          rate: calculateRate(currentData.buyPlan || 0, currentData.buyPlan || 0),
          loading: false,
        },
        projects: {
          count: currentData.projects || 0,
          rate: calculateRate(currentData.projects || 0, currentData.projects || 0),
          loading: false,
        },
        allMoney: {
          amount: currentData.allMoney || 0,
          rate: calculateRate(currentData.allMoney || 0, currentData.allMoney || 0),
          loading: false,
          currency: '€',
        },
      });

      fetchChartData();
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch traffic stats:', error);
      setTrafficStats((prev) =>
        Object.fromEntries(
          Object.keys(prev).map((k) => [k, { ...prev[k], loading: false }]),
        ),
      );
      setLoading(false);
    }
  };

  const fetchCountryData = async (metric = 'viewPage') => {
    try {
      setCountryDataLoading(true);
      const params = { ...getDateParams(), metric };
      const response = await axios.get('/api/dashboard/traffic-countries', { params });

      if (response.data && Array.isArray(response.data)) {
        const formattedData = response.data.map((item) => ({
          country: item.country.toUpperCase(),
          value: item.value,
          name: item.name || item.country,
        }));

        formattedData.sort((a, b) => b.value - a.value);

        const sortedByValue = [...formattedData].sort((a, b) => a.value - b.value);
        const dataLength = sortedByValue.length;

        if (dataLength > 0) {
          formattedData.forEach((item) => {
            const valuePosition = sortedByValue.findIndex((sorted) => sorted.value === item.value);
            let colorIndex = 4;
            if (dataLength > 1) {
              const percentage = valuePosition / (dataLength - 1);
              colorIndex = Math.min(8, Math.floor(percentage * 9));
            }
            item.colorIndex = colorIndex;
          });
        }

        setCountryData(formattedData);
      } else {
        setCountryData([]);
      }
    } catch (error) {
      console.error('Failed to fetch country stats:', error);
      setCountryData([]);
    } finally {
      setCountryDataLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      const params = getDateParams();
      const response = await axios.get('/api/dashboard/traffic-chart', { params });
      setChartData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
      setChartData([]);
    }
  };

  useEffect(() => {
    fetchTrafficStats();
    fetchCountryData(selectedMetric);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMetric, dateRange]);

  useEffect(() => {
    fetchChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  useEffect(() => {
    fetchTrafficStats();
    fetchCountryData(selectedMetric);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTooltipText = (countryInfo) => {
    if (!countryInfo) return 'Unknown: 0';

    const country = countryData.find(
      (item) =>
        item.country === countryInfo.countryCode ||
        item.country === (countryInfo.countryCode || '').toUpperCase(),
    );

    const countryName = country?.name || countryInfo.countryName || countryInfo.countryCode || 'Unknown';
    const value = countryInfo.countryValue || 0;

    let suffix = 'visitors';
    if (selectedMetric === 'registerUsers') suffix = 'users';
    else if (selectedMetric === 'buyPlan') suffix = 'purchases';
    else if (selectedMetric === 'projects') suffix = 'projects';
    else if (selectedMetric === 'allMoney') suffix = '€';

    return `${countryName}: ${value} ${suffix}`;
  };

  const renderStatBox = (key, title, value, loading) => {
    const isMoneyMetric = key === 'allMoney';
    const displayValue = isMoneyMetric ? `${value}€` : value;

    return (
      <StatCard
        title={title}
        icon={trafficMetricIcons[key]}
        value={displayValue}
        loading={loading}
        selected={selectedMetric === key}
        onClick={() => setSelectedMetric(key)}
        subtitle="Selected Period"
        rate={0}
      />
    );
  };

  const maxCountryValue = Math.max(...countryData.map((d) => d.value), 1);

  const statItems = [
    ['viewPage', 'UNIQUE VISITORS', trafficStats.viewPage.count, trafficStats.viewPage.loading],
    ['registerUsers', 'REGISTER USERS', trafficStats.registerUsers.count, trafficStats.registerUsers.loading],
    ['buyPlan', 'BUY PLAN', trafficStats.buyPlan.count, trafficStats.buyPlan.loading],
    ...(FEATURES.PROJECTS_ENABLED
      ? [['projects', 'PROJECTS', trafficStats.projects.count, trafficStats.projects.loading]]
      : []),
    ['allMoney', 'ALL MONEY', trafficStats.allMoney.amount, trafficStats.allMoney.loading],
  ];

  return (
    <Box>
      <Grid container spacing={DASHBOARD_STAT_GRID_SPACING} sx={acmeDashboardStatGridSx}>
        {statItems.map(([key, title, value, statLoading]) => (
          <Grid item xs={6} sm={6} md={statGridMd} key={key}>
            {renderStatBox(key, title, value, statLoading)}
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={DASHBOARD_CHART_GRID_SPACING}>
        <Grid item xs={12} md={6}>
          <Card sx={panelCardSx}>
            <CardContent sx={acmeDashboardCardContentSx}>
              <Typography variant="h6" sx={acmeDashboardSectionTitleSx}>
                {metricNames[selectedMetric]} by Country
              </Typography>
              <Typography sx={acmeDashboardSectionDescSx}>
                Geographic distribution of {metricNames[selectedMetric].toLowerCase()} for the selected date range
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  minHeight: { xs: 'auto', sm: '400px' },
                  height:
                    showAllCountries && countryData.length > 6
                      ? 'auto'
                      : { xs: 'auto', sm: '400px' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    flex: { xs: '1 1 auto', sm: '0 0 35%' },
                    pr: { xs: 0, sm: 2 },
                    maxHeight:
                      showAllCountries && countryData.length > 6
                        ? 'none'
                        : { xs: 'none', sm: '400px' },
                    overflowY:
                      showAllCountries && countryData.length > 6
                        ? 'visible'
                        : { xs: 'visible', sm: 'auto' },
                    mb: { xs: 3, sm: 0 },
                    minWidth: 0,
                  }}
                >
                  {countryDataLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <CircularProgress size={28} sx={{ color: acme.colors.iconDefault }} />
                    </Box>
                  ) : countryData.length > 0 ? (
                    <>
                      {(showAllCountries ? countryData : countryData.slice(0, 6)).map((item, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                maxWidth: '65%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {item.name || item.country}
                            </Typography>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 700 }}>
                              {item.value}
                              {selectedMetric === 'allMoney' && '€'}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: '100%',
                              height: 8,
                              bgcolor: acme.colors.muted,
                              borderRadius: acme.radius.sm,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                width: `${Math.min(100, (item.value / maxCountryValue) * 100)}%`,
                                height: '100%',
                                bgcolor: acmeNeutralColorScale[item.colorIndex ?? 4],
                              }}
                            />
                          </Box>
                        </Box>
                      ))}
                      {countryData.length > 6 && (
                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                          <Button variant="link" onClick={() => setShowAllCountries(!showAllCountries)}>
                            {showAllCountries ? 'Show less' : `Show all (${countryData.length})`}
                          </Button>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        backgroundColor: acme.colors.muted,
                        padding: 2,
                        borderRadius: acme.radius.sm,
                        border: `1px dashed ${acme.colors.border}`,
                      }}
                    >
                      <Typography sx={{ color: acme.colors.mutedForegroundSolid, mb: 0.5 }}>
                        No country data available
                      </Typography>
                      <Typography
                        sx={{ fontSize: '0.75rem', color: acme.colors.mutedForegroundSolid, textAlign: 'center' }}
                      >
                        Try changing the date range or selecting a different metric to see data
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    flex: { xs: '1 1 auto', sm: '0 0 65%' },
                    height: { xs: '300px', sm: '100%' },
                    overflow: 'hidden',
                    minHeight: { xs: '300px', sm: '400px' },
                    minWidth: 0,
                  }}
                >
                  {countryDataLoading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                      }}
                    >
                      <CircularProgress size={28} sx={{ color: acme.colors.iconDefault }} />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                      }}
                    >
                      <WorldMap
                        data={countryData.map((item) => ({
                          country: item.country,
                          value: item.value,
                          name: item.name || item.country,
                          colorIndex: item.colorIndex,
                        }))}
                        styleFunction={(context) => {
                          const countryItem = countryData.find(
                            (item) => item.country === (context.countryCode || '').toUpperCase(),
                          );
                          let fillColor = acme.colors.muted;
                          if (countryItem && countryItem.value > 0) {
                            fillColor = acmeNeutralColorScale[countryItem.colorIndex];
                          }
                          return {
                            fill: fillColor,
                            fillOpacity: 1,
                            stroke: acme.colors.border,
                            strokeWidth: 0.7,
                            strokeOpacity: 0.8,
                            cursor: 'pointer',
                          };
                        }}
                        color={acme.colors.iconDefault}
                        size="lg"
                        backgroundColor={acme.colors.background}
                        strokeOpacity={0.8}
                        borderColor={acme.colors.border}
                        tooltipBgColor={acme.colors.foreground}
                        tooltipTextColor={acme.colors.background}
                        valuePrefix=""
                        valueSuffix={selectedMetric === 'allMoney' ? '€' : ''}
                        tooltipTextFunction={getTooltipText}
                        containerStyle={{
                          width: '100%',
                          height: '100%',
                          maxWidth: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      />

                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 10,
                          right: 10,
                          display: 'flex',
                          flexDirection: 'column',
                          backgroundColor: 'rgba(255, 255, 255, 0.92)',
                          padding: 1,
                          borderRadius: acme.radius.sm,
                          border: `1px solid ${acme.colors.border}`,
                          boxShadow: acme.shadow.lg,
                          maxWidth: '150px',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, mb: 0.5 }}>
                          {metricNames[selectedMetric]}
                        </Typography>
                        {[
                          ['Low', 1],
                          ['Medium', 4],
                          ['High', 8],
                        ].map(([label, idx]) => (
                          <Box key={label} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                backgroundColor: acmeNeutralColorScale[idx],
                                mr: 1,
                                borderRadius: '2px',
                              }}
                            />
                            <Typography sx={{ fontSize: '0.6875rem', color: acme.colors.mutedForegroundSolid }}>
                              {label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={panelCardSx}>
            <CardContent sx={acmeDashboardCardContentSx}>
              <Typography variant="h6" sx={acmeDashboardSectionTitleSx}>
                Traffic Metrics Over Time
              </Typography>
              <Typography sx={acmeDashboardSectionDescSx}>
                Trends for all metrics during the selected date range
              </Typography>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                  <CircularProgress size={28} sx={{ color: acme.colors.iconDefault }} />
                </Box>
              ) : chartData.length > 0 ? (
                <Box sx={{ height: '400px', width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={acme.colors.border} />
                      <XAxis dataKey="date" tick={{ fill: acme.colors.mutedForegroundSolid, fontSize: 12 }} />
                      <YAxis tick={{ fill: acme.colors.mutedForegroundSolid, fontSize: 12 }} />
                      <RechartsTooltip
                        formatter={(value, name) => {
                          let formattedValue = value;
                          const formattedName = metricNames[name] || name;
                          if (name === 'allMoney') formattedValue = `${value}€`;
                          return [formattedValue, formattedName];
                        }}
                        labelFormatter={(label) => `Date: ${label}`}
                        contentStyle={{
                          borderRadius: acme.radius.sm,
                          border: `1px solid ${acme.colors.border}`,
                          fontSize: '0.8125rem',
                        }}
                      />
                      <Legend formatter={(value) => metricNames[value] || value} />
                      <Line
                        type="monotone"
                        dataKey="viewPage"
                        name="Unique Visitors"
                        stroke={acmeChartColors.viewPage}
                        activeDot={{ r: 6 }}
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="registerUsers"
                        name="Register Users"
                        stroke={acmeChartColors.registerUsers}
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="buyPlan"
                        name="Buy Plan"
                        stroke={acmeChartColors.buyPlan}
                        strokeWidth={2}
                        dot={false}
                      />
                      {FEATURES.PROJECTS_ENABLED && (
                        <Line
                          type="monotone"
                          dataKey="projects"
                          name="Projects"
                          stroke={acmeChartColors.projects}
                          strokeWidth={2}
                          dot={false}
                        />
                      )}
                      <Line
                        type="monotone"
                        dataKey="allMoney"
                        name="All Money"
                        stroke={acmeChartColors.allMoney}
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                  <Typography sx={{ color: acme.colors.mutedForegroundSolid }}>
                    No chart data available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardTraffics;
